import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CustomerVehiclePicker } from "@/components/CustomerVehiclePicker";
import { AppointmentAPI, BayAPI, BranchAPI, TechnicianAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const SERVICE_TYPES = [
    "general_service", "repair", "inspection", "bodywork", "diagnostic", "other",
];
const PRIORITIES = ["low", "normal", "high"];

// Build a datetime-local input value from a Date
function toLocal(dt) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}
function fromLocal(s) { return new Date(s).toISOString(); }

export default function AppointmentNewPage() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);
    const [branches, setBranches] = useState([]);
    const [bays, setBays] = useState([]);
    const [techs, setTechs] = useState([]);
    const [cv, setCV] = useState({ customer: null, vehicle: null });

    const now = new Date();
    const inOneHr = new Date(now.getTime() + 60 * 60 * 1000);
    const inTwoHr = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    const [form, setForm] = useState({
        branch_id: "",
        service_type: "general_service",
        description: "",
        priority: "normal",
        scheduled_start: toLocal(inOneHr),
        scheduled_end: toLocal(inTwoHr),
        bay_id: "",
        assigned_technician_user_ids: [],
        customer_notes: "",
        internal_notes: "",
        allow_outside_hours: false,
        source: "walk_in",
        estimated_duration_minutes: 60,
    });

    useEffect(() => {
        BranchAPI.list().then((b) => {
            setBranches(b);
            if (b.length && !form.branch_id) {
                const pref = user?.default_branch_id || b[0].id;
                setForm((f) => ({ ...f, branch_id: pref }));
            }
        });
        TechnicianAPI.list({ is_active: true }).then((t) => setTechs(t.items || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!form.branch_id) return;
        BayAPI.list({ branch_id: form.branch_id, is_active: true }).then((r) => setBays(r.items || []));
    }, [form.branch_id]);

    const visibleTechs = useMemo(() => {
        if (!form.branch_id) return techs;
        return techs.filter((t) => (t.branch_ids || []).includes(form.branch_id));
    }, [techs, form.branch_id]);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
    function toggleTech(uid) {
        set("assigned_technician_user_ids", form.assigned_technician_user_ids.includes(uid)
            ? form.assigned_technician_user_ids.filter((x) => x !== uid)
            : [...form.assigned_technician_user_ids, uid]);
    }

    async function save() {
        if (!cv.customer || !cv.vehicle) { toast.error("Pick a customer and a vehicle"); return; }
        setBusy(true);
        try {
            const body = {
                branch_id: form.branch_id,
                customer_id: cv.customer.id,
                vehicle_id: cv.vehicle.id,
                source: form.source,
                service_type: form.service_type,
                description: form.description || undefined,
                requested_services: [],
                scheduled_start: fromLocal(form.scheduled_start),
                scheduled_end: fromLocal(form.scheduled_end),
                bay_id: form.bay_id || undefined,
                assigned_technician_user_ids: form.assigned_technician_user_ids,
                estimated_duration_minutes: Number(form.estimated_duration_minutes) || undefined,
                priority: form.priority,
                customer_notes: form.customer_notes || undefined,
                internal_notes: form.internal_notes || undefined,
                allow_outside_hours: form.allow_outside_hours,
            };
            const a = await AppointmentAPI.create(body);
            toast.success("Appointment booked");
            nav(`/appointments/${a.id}`);
        } catch (e) {
            toast.error(extractError(e));
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-6 max-w-4xl animate-fade-in" data-testid="appointment-new-page">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">New appointment</h1>
                    <p className="mt-1 text-sm text-zinc-500">Book a service slot. Conflicts on bay/technician surface as 409 on save.</p>
                </div>
                <Link to="/appointments" className="text-sm text-zinc-500 hover:underline">← Back</Link>
            </header>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Customer &amp; vehicle</h2>
                <CustomerVehiclePicker value={cv} onChange={setCV} testid="appt-cvpicker" />
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">When &amp; where</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Branch" required>
                        <select value={form.branch_id} onChange={(e) => set("branch_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="appt-branch">
                            {branches.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Bay">
                        <select value={form.bay_id} onChange={(e) => set("bay_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="appt-bay">
                            <option value="">— No bay yet —</option>
                            {bays.map((b) => <option key={b.id} value={b.id}>{b.code} · {b.bay_type}</option>)}
                        </select>
                    </Field>
                    <Field label="Start" required>
                        <Input type="datetime-local" value={form.scheduled_start} onChange={(e) => set("scheduled_start", e.target.value)} data-testid="appt-start" />
                    </Field>
                    <Field label="End" required>
                        <Input type="datetime-local" value={form.scheduled_end} onChange={(e) => set("scheduled_end", e.target.value)} data-testid="appt-end" />
                    </Field>
                    <Field label="Service type">
                        <select value={form.service_type} onChange={(e) => set("service_type", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="appt-service-type">
                            {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                        </select>
                    </Field>
                    <Field label="Priority">
                        <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="appt-priority">
                            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </Field>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500">
                    <input id="ooh" type="checkbox" checked={form.allow_outside_hours} onChange={(e) => set("allow_outside_hours", e.target.checked)} data-testid="appt-ooh" />
                    <label htmlFor="ooh">Allow outside working hours (advisor / owner only)</label>
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Technicians ({form.assigned_technician_user_ids.length})</h2>
                {visibleTechs.length === 0 ? (
                    <p className="text-sm text-zinc-500">No technicians available for this branch.</p>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {visibleTechs.map((t) => (
                            <button key={t.id} type="button" onClick={() => toggleTech(t.user_id)} data-testid={`appt-tech-${t.user_id}`}
                                className={`rounded-md border px-2.5 py-1.5 text-xs ${form.assigned_technician_user_ids.includes(t.user_id) ? "border-brand-red bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/15 dark:text-brand-red" : "border-zinc-200 dark:border-zinc-800"}`}>
                                <span className="font-medium">{t.user?.name || t.employee_code}</span>
                                <span className="ml-1 text-zinc-500 font-mono">· {(t.skills || []).slice(0, 2).join(",")}</span>
                            </button>
                        ))}
                    </div>
                )}
            </Card>

            <Card className="p-6">
                <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Notes</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Description (visible to customer)">
                        <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="appt-description" />
                    </Field>
                    <Field label="Internal notes">
                        <Textarea value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} rows={3} data-testid="appt-internal-notes" />
                    </Field>
                </div>
            </Card>

            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
                <Button onClick={save} disabled={busy || !cv.customer || !cv.vehicle || !form.branch_id} className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="appt-save">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Book appointment
                </Button>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {label}{required && <span className="ml-0.5 text-brand-red">*</span>}
            </Label>
            {children}
        </div>
    );
}
